import { useCallback, useMemo, useState } from "react";
import { MotionConfig, useReducedMotion } from "framer-motion";

import { sendMessageToHost } from "~/utils/events";
import { useLabels } from "~/hooks/useLabels";
import { useMountEffect } from "~/hooks/useMountEffect";
import { isMobileNative } from "~/utils/helpers";
import {
  clearPreferences,
  getPreferences,
  migratePreferences,
  setPreferences,
} from "~/utils/preferences";

import { initialValue, UserContext } from "./context";
import { LoginModal } from "../loginModal/LoginModal";

import type { ReactNode } from "react";
import type { UserContextValue, UserPreferences } from "./context";

export interface UserContextProviderProps {
  children: ReactNode;
  currentUser: UserContextValue["currentUser"];
  hostContext: UserContextValue["hostContext"];
  defaultPreferences: Maybe<UserPreferences>;
}

const DEFAULT_LABELS = {
  "prompt.login.title": "Unlock the whole class experience...",
  "prompt.login.description":
    "Save your progress and get access to more features!",
};

export function UserContextProvider({
  children,
  currentUser,
  hostContext,
  defaultPreferences,
}: UserContextProviderProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const shouldReduceMotion = useReducedMotion();

  const reducedMotion = useMemo(
    () => Boolean(shouldReduceMotion),
    [shouldReduceMotion],
  );

  // Preferences are persisted in a cookie
  const storedPreferences = useMemo(() => {
    try {
      return getPreferences();
    } catch (error) {
      return clearPreferences();
    }
  }, []);

  const [preferenceState, setPreferenceState] = useState<UserPreferences>({
    // Hard-coded defaults
    ...initialValue.preferences,
    // Defaults from context (cookie)
    ...defaultPreferences,
    // Overrides
    reducedMotion,
    // Client-side values (cookie)
    ...storedPreferences,
  });

  const goToLogin = useCallback(() => {
    globalThis.location.href = `/account/login/?redirect=${globalThis.location.href}`;
  }, []);

  const goToRegister = useCallback(() => {
    globalThis.location.href = `/account/register/?redirect=${globalThis.location.href}`;
  }, []);

  const promptLogin = useCallback(() => {
    if (isMobileNative()) {
      sendMessageToHost({
        navigation: {
          presentLogin: {
            title: t("prompt.login.title"),
            description: t("prompt.login.description"),
          },
        },
      });
    } else {
      setIsLoginModalOpen(true);
    }
  }, [t]);

  const handleRequestLoginModalClose = useCallback(() => {
    setIsLoginModalOpen(false);
  }, []);

  const setPreference = useCallback(
    (key: keyof UserPreferences, value: UserPreferences[typeof key]) => {
      setPreferenceState((p) => {
        const newValue = {
          ...p,
          [key]: value,
        };

        // Persist the new preference value
        setPreferences(JSON.stringify(newValue));

        return newValue;
      });
    },
    [],
  );

  const value: UserContextValue = {
    currentUser,
    hostContext,
    preferences: preferenceState,
    goToLogin,
    goToRegister,
    promptLogin,
    setPreference,
  };

  useMountEffect(() => {
    try {
      migratePreferences();
    } catch (error) {}
  });

  return (
    <UserContext.Provider value={value}>
      <MotionConfig reducedMotion={reducedMotion ? "always" : "never"}>
        {children}

        <LoginModal
          isOpen={isLoginModalOpen}
          onRequestClose={handleRequestLoginModalClose}
        />
      </MotionConfig>
    </UserContext.Provider>
  );
}
